import { useTranslation } from 'react-i18next';
import { alignableObjectsInSelection } from '@peruselab/structure-editor/domain/objectAlignment';
import { Icon } from '../Icon';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';

export function SelectionActions({ doc }: { readonly doc: UseDrawingDocumentResult }) {
  const { t } = useTranslation();
  const objects = alignableObjectsInSelection(doc.document, doc.selection);
  const hasAtoms = doc.selectedTransformAtomIds.length > 0;
  const canRotate3d = objects.some((object) => object.kind === 'molecule' && object.atomIds.length > 1);
  return (
    <>
      <section className="rail-section">
        <h2>{t('rightRail.rotateFlip')}</h2>
        <button
          type="button"
          className="icon-button"
          aria-label={t('rightRail.cleanUpStructure')}
          title={t('rightRail.cleanUpStructure')}
          disabled={!canRotate3d || doc.isCleaningUp}
          aria-busy={doc.isCleaningUp}
          onClick={() => void doc.cleanUpSelection()}
        >
          <Icon name="polyline" />
        </button>
        {doc.cleanupError ? <p role="alert">{doc.cleanupError}</p> : null}
        <div className="rail-action-grid rotate-flip-actions">
          {(['left', 'right', 'flipHorizontal', 'flipVertical'] as const).map((kind) => {
            const label = kind === 'left' ? 'rotateLeft' : kind === 'right' ? 'rotateRight' : kind;
            return (
              <button
                key={kind}
                type="button"
                className={`icon-button ${kind === 'flipVertical' ? 'icon-rotate-90' : ''}`}
                title={t(`editorToolPalette.selection.${label}`)}
                aria-label={t(`editorToolPalette.selection.${label}`)}
                disabled={!hasAtoms}
                onClick={() => doc.transformSelection(kind)}
              >
                <Icon name={kind === 'left' ? 'rotateLeft' : kind === 'right' ? 'rotateRight' : 'flip'} />
              </button>
            );
          })}
        </div>
      </section>
      <section className="rail-section">
        <div className="rotation-heading">
          <h2>{t('rightRail.rotation3d')}</h2>
          <details className="rotation-info">
            <summary aria-label={t('rightRail.rotation3dInfo')} title={t('rightRail.rotation3dProjection')}>
              <Icon name="info" size="1rem" />
            </summary>
            <p>{t('rightRail.rotation3dProjection')}</p>
          </details>
        </div>
        <p className="rail-hint">{t('rightRail.rotation3dHint')}</p>
        <table className="rotation-axis-grid" aria-label={t('rightRail.rotation3d')}>
          <thead>
            <tr>
              <th scope="col">
                <span className="visually-hidden">{t('rightRail.rotationAxis')}</span>
              </th>
              <th scope="col">−15°</th>
              <th scope="col">+15°</th>
            </tr>
          </thead>
          <tbody>
            {(['x', 'y', 'z'] as const).map((axis) => (
              <tr key={axis}>
                <th scope="row">{axis.toUpperCase()}</th>
                {([-15, 15] as const).map((degrees) => (
                  <td key={degrees}>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={t('rightRail.rotateAxis', { axis: axis.toUpperCase(), degrees })}
                      title={t('rightRail.rotateAxis', { axis: axis.toUpperCase(), degrees })}
                      disabled={!canRotate3d}
                      onClick={() => doc.rotateSelection3d(axis, degrees)}
                    >
                      <Icon name={degrees < 0 ? 'rotateLeft' : 'rotateRight'} />
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
